<script setup lang="ts">
import { computed } from "vue";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import LocaleSwitcher from "../../shared/LocaleSwitcher.vue";
import RedeemButton from "../../roster/display/RedeemButton.vue";

interface Props {
  currentPage?: "list" | "create" | "edit" | "tournament";
  eventId?: string | number;
}

interface BreadcrumbItem {
  name: string;
  route: { name: string; params?: { id: string | number } };
  active: boolean;
}

const props = defineProps<Props>();
const route = useRoute();
const { t } = useI18n();

// Generate a unique key for this breadcrumb instance
const breadcrumbKey = computed(() => {
  return `breadcrumbs-${String(route.name)}-${route.params.id || 'list'}`;
});

const breadcrumbs = computed<BreadcrumbItem[]>(() => {
  const items: BreadcrumbItem[] = [
    {
      name: t("breadcrumbs.events_list"),
      route: { name: "list" },
      active: route.name === "list",
    },
  ];

  // Determine current page from route if not provided
  const currentPage = props.currentPage ||
    (route.name === "new_event" ? "create" :
     route.name === "edit_event" ? "edit" :
     route.name === "tournament_management" ? "tournament" : "list");

  if (currentPage === "create" || route.name === "new_event") {
    items.push({
      name: t("breadcrumbs.create_event"),
      route: { name: "new_event" },
      active: route.name === "new_event",
    });
  }

  if (currentPage === "edit" || route.name === "edit_event") {
    const eventId = props.eventId || (Array.isArray(route.params.id) ? route.params.id[0] : route.params.id);
    items.push({
      name: t("breadcrumbs.edit_event"),
      route: { name: "edit_event", params: { id: eventId! } },
      active: route.name === "edit_event",
    });
  }

  if (currentPage === "tournament" || route.name === "tournament_management") {
    const eventId = props.eventId || (Array.isArray(route.params.id) ? route.params.id[0] : route.params.id);
    items.push({
      name: t("breadcrumbs.tournament_management"),
      route: { name: "tournament_management", params: { id: eventId! } },
      active: route.name === "tournament_management",
    });
  }

  return items;
});
</script>

<template>
  <teleport to="header#breadcrumbs" :key="breadcrumbKey">
    <div class="breadcrumbs">
      <router-link
        v-for="(item, index) in breadcrumbs"
        :key="index"
        :to="item.route"
        class="breadcrumbs-item"
        :class="{ disabled: !item.active }"
      >
        {{ item.name }}
      </router-link>
    </div>

    <div class="header-actions">
      <redeem-button :mobile="false"></redeem-button>

      <locale-switcher />
    </div>
  </teleport>
</template>

<style scoped>
.header-action {
  font-weight: bold;
  min-width: 40px;
  font-size: 20pt;
  text-align: center;
}
</style>
